import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, dropdownMenuItemDestructiveClass } from 'components/ui/dropdown-menu'
import { MoreHoriz } from 'iconoir-react';
import { ConversationMenuIcon } from 'components/blocks/layout/conversation-menu-icon'
import { CustomDialog } from "components/blocks/custom-dialog";
import { DebouncedInput } from "components/blocks/debounced-input/debounced-input";
import { useProjectActions } from '../hooks/use-project-actions';
import { useTranslation } from "react-i18next";
import { useEffect, useState } from 'react';
import { DEFAULT_PROJECT_COLOR, ProjectColorSwatches } from './project-color-swatches';

interface ProjectActionsDropdownProps {
    fetchedProject: any;
}

function ProjectActionsDropdown({ fetchedProject }: ProjectActionsDropdownProps) {
    const { remove, rename, isDeleteLoading, isRenameLoading } = useProjectActions(fetchedProject?.id);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [selectedColor, setSelectedColor] = useState<string>(DEFAULT_PROJECT_COLOR);
    const [actionType, setActionType] = useState<"delete" | "rename" | null>(null);
    const { t } = useTranslation();

    const handleRename = async () => {
        if (newTitle.trim() === "") return;
        await rename(newTitle, selectedColor);
        setIsRenameDialogOpen(false);
    };

    const handleRemove = async () => {
        await remove();
        setIsConfirmDialogOpen(false);
    };

    const handleConfirmAction = () => {
        if (actionType === "rename") handleRename();
        if (actionType === "delete") handleRemove();
    };

    useEffect(() => {
        if (fetchedProject?.name) {
            setNewTitle(fetchedProject.name);
        }
        setSelectedColor(fetchedProject?.color || DEFAULT_PROJECT_COLOR);
    }, [fetchedProject?.name, fetchedProject?.color]);

    if (!fetchedProject) return null;

    return (
        <>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        aria-label={t('MORE_ACTIONS') || 'More actions'}
                        className="inline-flex size-6 shrink-0 -rotate-90 flex-col items-center justify-center gap-[7px] overflow-hidden rounded px-3 py-1 text-[#3D3D3D] aspect-square hover:bg-muted-foreground/10 dark:text-true-white"
                    >
                        <MoreHoriz className="size-5 shrink-0 rotate-90" strokeWidth={1.5} />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8}>
                    <DropdownMenuGroup>
                        <DropdownMenuItem
                            onClick={() => {
                                setActionType("rename");
                                setIsRenameDialogOpen(true);
                            }}
                        >
                            <ConversationMenuIcon optionId="rename" />
                            <span>{t("EDIT_PROJECT")}</span>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                            className={dropdownMenuItemDestructiveClass}
                            onClick={() => {
                                setActionType("delete");
                                setIsConfirmDialogOpen(true);
                            }}
                        >
                            <ConversationMenuIcon optionId="delete" />
                            <span>{t("DELETE_PROJECT")}</span>
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                </DropdownMenuContent>
            </DropdownMenu>

            <CustomDialog
                isOpen={isConfirmDialogOpen}
                isLoading={isDeleteLoading}
                setIsOpen={setIsConfirmDialogOpen}
                title={t("CONFIRM_ACTION")}
                onConfirm={handleConfirmAction}
                confirmText={t("YES")}
                cancelText={t("CANCEL")}
                description={""}
            >
                <p>{t("CONFIRM_ACTION_DESCRIPTION")}</p>
            </CustomDialog>

            <CustomDialog
                isOpen={isRenameDialogOpen}
                isLoading={isRenameLoading}
                setIsOpen={setIsRenameDialogOpen}
                title={t("RENAME")}
                onConfirm={handleConfirmAction}
                confirmText={t("SAVE")}
                cancelText={t("CANCEL")}
                isConfirmDisabled={newTitle.trim() === ""}
                description={""}
            >
                <DebouncedInput
                    placeholder={t("ENTER_NEW_TITLE")}
                    initialValue={newTitle}
                    onDebouncedChange={setNewTitle}
                    className="h-10 w-full rounded-3xl border border-primary bg-background pr-8 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <div className="mt-4 flex w-full flex-col items-start gap-4">
                    <label className="font-sans text-sm font-bold not-italic leading-[120%] text-grey-700 dark:text-true-white">
                        {t('COLOR') || 'Color'}
                    </label>
                    <ProjectColorSwatches selectedColor={selectedColor} onSelect={setSelectedColor} />
                </div>
            </CustomDialog>
        </>
    )
}

export default ProjectActionsDropdown
